import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Container, Card, Button, Spinner, Row, Col, Modal } from 'react-bootstrap';
import SEOHead from '../components/SEOHead';
import { ArrowLeft, Download, Star, Book, Trophy, Award } from 'react-bootstrap-icons';
import { db } from '../firebase';
import cubegonLogo from '../assets/cubegon-logo.png';
import whiteLogo from '../assets/whitebglogo.png';
import qrCode from '../assets/playstoreqr.png';
import { collection, query, where, getDocs } from 'firebase/firestore';
import '../App.css';

const LinksSection = ({ title, items, icon }) => {
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    <div className="resource-section">
      <h6 className="resource-section-title">
        <span className="icon">{icon}</span>
        {title}
      </h6>
      <div className="resource-list">
        {items.map((item, idx) => (
          <div key={idx} className="resource-item">
            <h6 className="resource-item-title">{item.title}</h6>
            <a
              href={item.url}
              className="resource-item-btn"
              target="_blank" rel="noopener noreferrer"
            >
              Open
            </a>
          </div>
        ))}
      </div>
    </div>
  );
};

const NotesSection = ({ notes }) => {
  if (!Array.isArray(notes) || notes.length === 0) return null;
  return (
    <div className="resource-section">
      <h6 className="resource-section-title">
        <span className="icon">📌</span>
        Notes
      </h6>
      <div>
        {notes.map((n) => (
          <div key={n.id} style={{ width: '100%' }}>
            <div className="content-text" dangerouslySetInnerHTML={{ __html: n.content }} />
          </div>
        ))}
      </div>
    </div>
  );
};

const ChapterResources = () => {
  const { grade, slug } = useParams();
  const navigate = useNavigate();
  const [chapter, setChapter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState([]);
  const [showAccelerateModal, setShowAccelerateModal] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        // find chapter by slug of chapterTitle
        const rq = query(collection(db, 'resources'));
        const rs = await getDocs(rq);
        const toSlug = (s) => (s || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
        const match = rs.docs.find(d => toSlug((d.data().chapterTitle || d.data().title || '')) === slug);
        if (match) {
          setChapter({ id: match.id, ...match.data() });
        }
        const chapterId = match ? match.id : null;
        // also fetch notes linked to this chapter
        const nq = chapterId ? query(collection(db, 'notes'), where('chapterId', '==', chapterId)) : null;
        if (nq) {
          const ns = await getDocs(nq);
          setNotes(ns.docs.map(d => ({ id: d.id, ...d.data() })));
        }
      } finally {
        setLoading(false);
      }
    };
    if (slug) load();
  }, [slug]);

  useEffect(() => {
    // Always show on each visit/refresh for any chapter
    setShowAccelerateModal(true);
  }, [slug]);

  if (loading) {
    return (
      <div className="modern-class-page">
        <Container className="py-5 text-center">
          <Spinner animation="border" variant="primary" />
        </Container>
      </div>
    );
  }

  if (!chapter) {
    return (
      <div className="modern-class-page">
        <Container className="py-5 text-center">
          <p>Chapter not found.</p>
          <Button as={Link} to={`/class/${grade}`} variant="outline" className="class-back-btn">
            <ArrowLeft className="me-2" /> Back to Class {grade}
          </Button>
        </Container>
      </div>
    );
  }

  return (
    <div className="modern-class-page">
      <SEOHead
        title={`${chapter.chapterTitle || chapter.title} – Class ${grade} Notes`}
        description={notes[0]?.seoDescription || ''}
        keywords={(notes[0]?.seoKeywords || []).join(', ')}
        canonical={`/class/${grade}/chapter/${slug}`}
        ogType="article"
      />
      <section className="class-header-section">
        <Container>
          <Modal show={showAccelerateModal} onHide={() => setShowAccelerateModal(false)} centered>
            <Card style={{ background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)', color: 'white' }}>
              <Card.Body>
                <div className="d-flex justify-content-end">
                  <button
                    aria-label="Close"
                    className="btn btn-sm btn-light"
                    onClick={() => setShowAccelerateModal(false)}
                  >
                    ✕
                  </button>
                </div>
                <Row className="align-items-center g-4">
                  <Col xs={12} md={7}>
                    <h3 className="mb-2">Ready to accelerate your learning?</h3>
                    <p className="mb-3" style={{ opacity: 0.95 }}>
                      Get AI-powered help, personalized study plans, and earn rewards for staying consistent.
                    </p>
                    <div className="d-flex flex-column flex-md-row gap-2">
                      <Button
                        variant="light"
                        className="w-100 w-md-auto"
                        onClick={() => window.open('https://play.google.com/store/apps/details?id=com.cubegon.com', '_blank')}
                      >
                        <Download className="me-1" /> Get the App
                      </Button>
                      <Button variant="outline-light" className="w-100 w-md-auto" onClick={() => setShowAccelerateModal(false)}>
                        Continue to chapter
                      </Button>
                    </div>
                  </Col>
                  <Col xs={12} md={5}>
                    <div style={{ background: 'white', borderRadius: 12, padding: 12, textAlign: 'center', maxWidth: 240, margin: '0 auto' }}>
                      <img src={qrCode} alt="Scan to download Cubegon" style={{ width: '100%', maxWidth: 180, height: 'auto' }} />
                      <div style={{ color: '#0f172a', fontWeight: 600, marginTop: 8 }}>Scan to install</div>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Modal>
          <Card 
            className="guide-header-card"
            style={{
              background: 'linear-gradient(180deg, var(--bg-primary), rgba(37,99,235,0.03))',
              border: '1px solid var(--border-color)'
            }}
          >
            <Row className="align-items-center g-4">
              <Col md={8}>
                <div>
                  <span className="guide-badge d-inline-flex align-items-center gap-2">
                    Chapter
                  </span>
                  <h1 className="guide-title mb-2">{chapter.chapterTitle || chapter.title}</h1>
                  <p className="guide-subtitle mb-0" style={{ color: 'var(--text-muted)' }}>
                    Curated resources and notes to master this chapter.
                  </p>
                </div>
              </Col>
              <Col md={4} className="text-md-end">
                <div className="cubegon-branding ms-md-auto" style={{ maxWidth: 360 }}>
                  <div className="d-flex align-items-center justify-content-md-end justify-content-start mb-2">
                    <img src={cubegonLogo} alt="Cubegon" style={{ height: 28 }} />
                    <span className="ms-2" style={{ fontWeight: 600, color: 'var(--text-secondary)' }}>by Cubegon</span>
                  </div>
                  <div className="d-flex flex-wrap gap-2 justify-content-md-end justify-content-start">
                    <span className="badge rounded-pill" style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', color: 'var(--text-secondary)' }}>
                      <Star className="me-1" /> AI-Powered Learning
                    </span>
                    <span className="badge rounded-pill" style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', color: 'var(--text-secondary)' }}>
                      <Book className="me-1" /> Reward Based Learning
                    </span>
                    <span className="badge rounded-pill" style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', color: 'var(--text-secondary)' }}>
                      <Trophy className="me-1" /> Track Your Progress
                    </span>
                  </div>
                  <div className="mt-3">
                    <Button 
                      variant="primary" 
                      size="sm"
                      onClick={() => window.open('https://play.google.com/store/apps/details?id=com.cubegon.com', '_blank')}
                    >
                      <Download className="me-1" /> Download App
                    </Button>
                  </div>
                </div>
              </Col>
            </Row>
            <hr className="mt-4 mb-3" />
            <div>
              <Button onClick={() => navigate(`/class/${grade}`)} variant="outline" className="class-back-btn">
                <ArrowLeft className="me-2" /> Back to Class {grade}
              </Button>
            </div>
          </Card>
        </Container>
      </section>

      <section className="detailed-guide-section">
        <Container>
          <Card className="guide-content-card p-4">
            <NotesSection notes={notes} />
            <LinksSection title="Other Notes" items={chapter.notes} icon="📄" />
            <LinksSection title="Questions" items={chapter.questions} icon="❓" />
            <LinksSection title="Lectures" items={chapter.lectures} icon="🎥" />
          </Card>
        </Container>
      </section>

      {/* Cubegon CTA Section */}
      <section className="cta-section">
        <Container>
          <Card className="cta-card">
            <Card.Body className="text-center">
              <Award className="cta-icon mb-3" />
              <h3 className="cta-title">Start Learning & Earning with Cubegon</h3>
              <p className="cta-description">
                Unlock AI-powered study help, gamified rewards, and progress tracking tailored for your class.
              </p>
              <div className="cta-buttons">
                <button 
                  className="btn btn-primary btn-lg me-3"
                  onClick={() => window.open('https://play.google.com/store/apps/details?id=com.cubegon.com', '_blank')}

                >
                  <Download className="me-2" /> Download App
                </button>
                <button 
                  className="btn btn-outline-primary btn-lg"
                  onClick={() => navigate(`/class/${grade}`)}
                >
                  Explore More Chapters
                </button>
              </div>
            </Card.Body>
          </Card>
        </Container>
      </section>

      {/* Footer */}
      <footer className="cubegon-footer">
        <div className="cubegon-footer-content">
          <div className="cubegon-footer-main">
            <div className="cubegon-footer-logo">
              <img src={whiteLogo} alt="Cubegon" />
            </div>

            <div className="cubegon-footer-download">
              <h3>Download the App</h3>
              <div className="cubegon-footer-qr">
                <img src={qrCode} alt="QR code" />
              </div>
            </div>
          </div>

          <div className="cubegon-footer-bottom">
            <p className="cubegon-footer-text">Made in 🇮🇳 with ❤️</p>
            <p className="cubegon-footer-copyright">© {new Date().getFullYear()} Numeric Education Pvt. Ltd. All rights reserved.</p>
            <div className="cubegon-footer-links">
              <a href="/privacy-policy">Privacy Policy</a>
              <a href="/terms-and-conditions">Terms & Conditions</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ChapterResources;



